import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Users } from "lucide-react";
import { useSumoImages } from "@/hooks/useSumoImages";
import { gtag } from "@/utils/gtag";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

interface SumoProductCardProps {
  sumo: {
    id: string;
    title: string;
    description: string;
    price: string;
    features: string[];
    quantite?: number;
  };
  onReservation: (sumoId: string) => void;
  hasDatesSelected?: boolean;
  availableCount?: number;
}

export const SumoProductCard = ({ sumo, onReservation, hasDatesSelected = true, availableCount }: SumoProductCardProps) => {
  const { imageUrls, isLoading } = useSumoImages(sumo.id);
  const [showDateAlert, setShowDateAlert] = useState(false);

  const displayImage = imageUrls.length > 0 ? imageUrls[0] : '/placeholder.svg';

  return (
    <Card className="overflow-hidden shadow-card hover:shadow-playful transition-all duration-300 flex flex-col h-full border-2 border-secondary/20">
      {/* Image */}
      <div className="relative h-56 overflow-hidden cursor-pointer">
        {isLoading ? (
          <div className="w-full h-full bg-muted animate-pulse" />
        ) : (
          <img
            src={displayImage}
            alt={sumo.title}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            loading="lazy"
            onError={(e) => { e.currentTarget.src = '/placeholder.svg'; }}
          />
        )}
        <div className="absolute top-3 left-3">
          <Badge className="gradient-primary text-white border-0 font-bold text-base px-3 py-1">
            {sumo.price}
          </Badge>
        </div>
        <div className="absolute top-3 right-3">
          <Badge variant="secondary" className="bg-secondary/90 text-white border-0">
            <Users className="w-3 h-3 mr-1" />
            Sumo
          </Badge>
        </div>
      </div>

      <CardHeader>
        <div className="flex justify-between items-start">
          <CardTitle className="text-xl font-display font-bold text-primary">
            {sumo.title}
          </CardTitle>
          <div className="flex gap-2 items-center ml-2">
            {hasDatesSelected && availableCount !== undefined && (sumo.quantite ?? 1) > 1 && (
              <Badge variant={availableCount <= 1 ? "destructive" : "default"} className="text-xs whitespace-nowrap">
                {availableCount}/{sumo.quantite} dispo
              </Badge>
            )}
            {!hasDatesSelected && (sumo.quantite ?? 1) > 1 && (
              <Badge variant="secondary" className="text-xs whitespace-nowrap">
                Stock: {sumo.quantite}
              </Badge>
            )}
            <Badge variant="outline">Adultes</Badge>
          </div>
        </div>
        <p className="text-muted-foreground">{sumo.description}</p>
      </CardHeader>

      <CardContent className="space-y-4 flex-1 flex flex-col">
        <div className="flex-1">
          <span className="font-semibold text-sm">Inclus:</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {sumo.features.map((feature, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {feature}
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex flex-col xs:flex-row gap-3 mt-auto">
          <Button
            className="flex-1 min-h-[48px] text-base"
            onClick={() => {
              if (!hasDatesSelected) {
                setShowDateAlert(true);
                return;
              }
              gtag('event', 'begin_checkout', {
                currency: 'EUR',
                value: parseInt(sumo.price) || 50,
                items: [{
                  item_id: sumo.id,
                  item_name: sumo.title,
                  item_category: 'Sumo Gonflable',
                  price: parseInt(sumo.price) || 50,
                  quantity: 1
                }]
              });
              onReservation(sumo.id);
            }}
          >
            <Calendar className="w-4 h-4 mr-2" />
            <span className="xs:hidden">Réserver</span>
            <span className="hidden xs:inline">Demande de réservation</span>
          </Button>
          <Button variant="outline" className="flex-1 min-h-[48px] text-base" asChild>
            <Link to="/sumos" className="flex items-center justify-center">
              Voir plus
            </Link>
          </Button>
        </div>
      </CardContent>

      <AlertDialog open={showDateAlert} onOpenChange={setShowDateAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>📅 Dates requises</AlertDialogTitle>
            <AlertDialogDescription>
              Pour faire une demande de réservation, veuillez d'abord sélectionner vos dates d'événement en haut de la page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => {
              setShowDateAlert(false);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
              Choisir mes dates
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
